/* global google */
import * as React from "react";
import apiClient from "services/axios";
import fieldProps from "./fieldProps";

const normalizeSearchValue = (value) =>
  (value || "")
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const buildCityLookupKey = (city, province) => {
  const normalizedCity = normalizeSearchValue(city)
  const normalizedProvince = (province || '').toString().trim().toUpperCase()

  return `${normalizedCity}|${normalizedProvince}`
}

const getHydraItems = response => response?.data?.['hydra:member'] || response?.data?.items || response?.data || []

const getCityCap = item => {
  const primaryCap = (item?.cap || '').toString().trim()
  if (primaryCap) return primaryCap

  const secondaryCap = (item?.cap2 || item?.cap_2 || '').toString().trim()
  if (secondaryCap) return secondaryCap

  return null
}

class AutocompleteCity extends React.Component {
  constructor(props) {
    super(props);
    // Inizializza searchText con il valore passato tramite props o con una stringa vuota
    this.state = {
      searchText: props.value || "", // Usa props.value se disponibile
      sessionToken: null, // Token di sessione per le chiamate
      suggestions: [], // Suggerimenti locali arricchiti da italy_cities/italy_geo
      isSearchingLocal: false, // Stato ricerca locale
      debounceTimer: null, // Timer per debounce
      isFocused: false, // Stato focus input
    };
    this.autocomplete = null;
    this.placeChangedListener = null;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.value !== undefined && nextProps.value !== prevState.searchText) {
      return {
        searchText: nextProps.value, // Aggiorna il valore dell'input
      };
    }
    return null; // Nessuna modifica
  }

  componentDidMount() {
    // Non triggerare suggerimenti all'apertura pagina; attendi focus/typing
  }
  
  componentDidUpdate(prevProps, prevState) {
    const showLocalNow = this.state.isFocused && this.state.searchText.length >= 3 && (this.state.suggestions?.length || 0) > 0;
    const showLocalPrev = prevState.isFocused && prevState.searchText.length >= 3 && (prevState.suggestions?.length || 0) > 0;
    if (showLocalNow !== showLocalPrev) {
      this.setPacHidden(showLocalNow);
    }
  }
  
  componentWillUnmount() {
    this.cleanup();
  }

  cleanup = () => {
    if (this.placeChangedListener) {
      google.maps.event.removeListener(this.placeChangedListener);
    }
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = null;
    }
    const { debounceTimer } = this.state;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    this.setPacHidden(false);
    this.resetSessionToken();
  };

  // Gestione visibilità pac-container di Google per evitare sovrapposizioni
  addPacHiderStyle = () => {
    const id = 'pac-hider-style';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.innerHTML = `body.performa-hide-pac .pac-container { display: none !important; }`;
      document.head.appendChild(style);
    }
  };

  setPacHidden = (hidden) => {
    this.addPacHiderStyle();
    if (hidden) document.body.classList.add('performa-hide-pac');
    else document.body.classList.remove('performa-hide-pac');
  };

  initializeAutocomplete = () => {
    const { types = ["(cities)"] } = this.props;
  
    const options = {
      types
    };
  
    // Collega Autocomplete all'input solo se ci sono almeno 3 caratteri
    if (!this.autocomplete && this.state.searchText.length >= 3) {
      this.autocomplete = new google.maps.places.Autocomplete(this.input, options);
  
      // Listener per la selezione di un luogo
      this.placeChangedListener = this.autocomplete.addListener("place_changed", this.handlePlaceChanged);
    }
  };  

  ensureGoogleWithSession = () => {
    if (!this.state.sessionToken) {
      this.generateSessionToken();
    }
    if (this.autocomplete) {
      this.autocomplete.setOptions({ sessionToken: this.state.sessionToken });
    } else if (window.google && this.state.searchText.length >= 3) {
      this.initializeAutocomplete();
      if (this.autocomplete) {
        this.autocomplete.setOptions({ sessionToken: this.state.sessionToken });
      }
    }
  };

  handleFocus = () => {
    this.setState({ isFocused: true }, () => {
      if (this.state.searchText.length >= 3) {
        this.searchLocalCities(this.state.searchText).then(() => {
          if (window.google && (!this.state.suggestions || this.state.suggestions.length === 0)) {
            this.ensureGoogleWithSession();
          }
        });
      }
    });
  };

  handleBlur = () => {
    // Ritarda leggermente per consentire click su suggerimento (gestito con onMouseDown)
    setTimeout(() => {
      this.setState({ isFocused: false, suggestions: [] });
      this.setPacHidden(false);
    }, 100);
  };

  searchLocalCities = async (query) => {
    // Cerca prima in italy_cities per recuperare CAP/codice comune,
    // poi arricchisce il risultato con italy_geo per lat/lng.
    this.setState({ isSearchingLocal: true });
    try {
      const [citiesResponse, geoResponse] = await Promise.allSettled([
        apiClient.get('/api/italy_cities', { params: { comune: query, itemsPerPage: 50 } }),
        apiClient.get('/api/italy_geos', { params: { comune: query, itemsPerPage: 50 } }),
      ])

      const cities = citiesResponse.status === 'fulfilled' && Array.isArray(getHydraItems(citiesResponse.value))
        ? getHydraItems(citiesResponse.value)
        : []
      const geoItems = geoResponse.status === 'fulfilled' && Array.isArray(getHydraItems(geoResponse.value))
        ? getHydraItems(geoResponse.value)
        : []

      const geoByCity = geoItems.reduce((acc, item) => {
        const key = buildCityLookupKey(item?.comune, item?.provincia)
        if (!acc[key]) {
          acc[key] = item
        }
        return acc
      }, {})

      const suggestions = cities.map(item => {
        const geoItem = geoByCity[buildCityLookupKey(item?.comune, item?.provincia)]

        return {
          label: item?.comune || '',
          code: item?.codFisco || item?.cod_fisco || '',
          lat: geoItem?.lat ? Number(geoItem.lat) : null,
          lng: geoItem?.lng ? Number(geoItem.lng) : null,
          prov: item?.provincia || null,
          cap: getCityCap(item),
          country: 'Italia',
          countryCode: 'IT',
        }
      })
      .filter(item => item.label)

      // Ordina per rilevanza rispetto alla query: exact match, prefix, parola intera, contiene, poi per lunghezza/alfabeto
      const normalize = normalizeSearchValue
      const nq = normalize(query);
      const wordBoundaryRe = new RegExp(`(^|[\
\s'\-])${nq}`);
      const rank = (label) => {
        const nl = normalize(label);
        if (nl === nq) return 0; // match esatto
        if (nl.startsWith(nq)) return 1; // prefisso
        if (wordBoundaryRe.test(nl)) return 2; // inizio parola
        if (nl.includes(nq)) return 3; // contiene
        return 4; // altro
      };

      suggestions.sort((a, b) => {
        const ra = rank(a.label);
        const rb = rank(b.label);
        if (ra !== rb) return ra - rb;
        const la = a.label ? a.label.length : 0;
        const lb = b.label ? b.label.length : 0;
        if (la !== lb) return la - lb; // preferisci nome più corto (es. "Roma")
        if (a.label === b.label && a.prov && b.prov) return a.prov.localeCompare(b.prov);
        return (a.label || '').localeCompare(b.label || '', 'it', { sensitivity: 'base' });
      });
      this.setState({ suggestions });
    } catch (e) {
      // In caso di errore, non bloccare: svuota suggerimenti e lascia Google gestire
      console.warn('Errore ricerca comuni locali:', e);
      this.setState({ suggestions: [] });
    } finally {
      this.setState({ isSearchingLocal: false });
    }
  };

  generateSessionToken = () => {
    // Genera un nuovo session token
    const token = new google.maps.places.AutocompleteSessionToken();
    console.log("Session Token Generated:", token);
    this.setState({ sessionToken: token });
  };

  resetSessionToken = () => {
    console.log("Session Token Reset");
    this.setState({ sessionToken: null });
  };

  handlePlaceChanged = () => {
    const { onChange } = this.props;
    const place = this.autocomplete.getPlace();
    if (!place || !place.address_components) return;

    const values = this.extractPlaceDetails(place);
    console.log(values,'--',place);
    
    onChange(values);

    this.setState({ searchText: values.label });

    // Termina la sessione dopo la selezione del luogo
    this.resetSessionToken();
  };

  extractPlaceDetails = (place) => {
    const components = place.address_components || [];
    const details = {
      prov: null,
      countryCode: null,
      country: null,
      label: null,
      cap: null,
      lat: place.geometry?.location.lat() || null,
      lng: place.geometry?.location.lng() || null,
    };

    components.forEach((item) => {
      if (item.types.includes("locality")) {
        details.label = item.long_name || "";
      } else if (item.types.includes('administrative_area_level_3') && !(details.label)) {
        details.label = item.long_name
      } else if (item.types.includes("administrative_area_level_2")) {
        details.prov = item.short_name || "";
      } else if (item.types.includes("country")) {
        details.countryCode = item.short_name || "";
        details.country = item.long_name || "";
      } else if (item.types.includes("postal_code")) {
        details.cap = item.long_name || "";
      }
    });

    return details;
  };

  handleInputChange = (e) => {
    const { onChange } = this.props;
    const rawValue = e.target.value;
    const trimmedValue = rawValue.trim();

    // Aggiorna input e avvia debounce della ricerca locale
    this.setState({ searchText: rawValue }, () => {
      const { debounceTimer } = this.state;
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (trimmedValue.length >= 3) {
        const newTimer = setTimeout(async () => {
          await this.searchLocalCities(trimmedValue);
          // Se non ci sono suggerimenti locali, abilita Google Autocomplete
          if (!this.state.suggestions || this.state.suggestions.length === 0) {
            this.ensureGoogleWithSession();
          }
        }, 250);
        this.setState({ debounceTimer: newTimer });
      } else {
        this.setState({ suggestions: [] });
        this.setPacHidden(false);
      }
    });

    // Genera un nuovo token solo se non esiste già
    if (!this.state.sessionToken) {
      this.generateSessionToken();
    }

    // Aggiorna Google Autocomplete con sessione, se attivo
    if (this.autocomplete) {
      this.autocomplete.setOptions({ sessionToken: this.state.sessionToken });
    }

    onChange({ label: rawValue });
  };

  handleLocalSuggestionClick = (suggestion) => {
    const { onChange } = this.props;
    const values = {
      prov: suggestion.prov || null,
      countryCode: suggestion.countryCode,
      country: suggestion.country,
      label: suggestion.label,
      cap: suggestion.cap || null,
      lat: suggestion.lat,
      lng: suggestion.lng,
      code: suggestion.code,
    };
    onChange(values);
    this.setState({ searchText: suggestion.label, suggestions: [] });
    this.setPacHidden(false);
    this.resetSessionToken();
  };

  render() {
    const { name } = this.props;
    const { searchText, suggestions, isSearchingLocal } = this.state;

    return (
      <div style={{ position: 'relative' }}>
        <input
          name={name}
          className="form-control input-sm"
          ref={(input) => {
            this.input = input;
          }}
          value={searchText}  // Assicurati che il valore venga gestito correttamente dallo stato
          onChange={this.handleInputChange}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          placeholder="Inserisci una città"
        />
        {this.state.isFocused && searchText && searchText.length >= 3 && suggestions && suggestions.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10000, background: '#fff', border: '1px solid #ddd', borderRadius: 4, width: '100%', maxHeight: 240, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
            {suggestions.map((s) => (
              <div
                key={`${s.code || s.label}`}
                style={{ padding: '6px 8px', cursor: 'pointer' }}
                onMouseDown={(e) => {
                  // onMouseDown per evitare blur prima del click
                  e.preventDefault();
                  this.handleLocalSuggestionClick(s);
                }}
              >
                {s.label}
              </div>
            ))}
          </div>
        )}
        {this.state.isFocused && isSearchingLocal && (
          <div style={{ color: '#888'}} />
        )}
      </div>
    );
  }
}

export default fieldProps(AutocompleteCity);